// app/src/lib/revenue/settledSales.js — month totals, split in-house vs delivery.
//
// HISTORY (2026-08-04): syncFromDeposits used to record each bank deposit as a
// salesEntry keyed by its POSTING date, so this module had to run the payout
// schedule backwards at READ time to work out which month a deposit was earned
// in. That had two costs: a per-DAY view was impossible (weekends read $0), and
// the same deposit could settle differently depending on how wide a window the
// caller asked for — the Today tile and the Sales report genuinely disagreed
// once because of it.
//
// Deposits are now attributed to their SALE DAYS once, at write time, in
// syncFromDeposits (see depositSchedule.attributeStreamByDay). Every salesEntry
// is therefore already sale-dated, whatever its source, and a month total is a
// plain sum of the days inside it. That is the whole module now — the inverse
// lives in exactly one place, and days and months can no longer disagree.
//
// `rawNetSalesCents` is kept on the result for API compatibility; it is now
// identical to netSalesCents (there is no longer a "naive deposit-dated" view to
// contrast with).

import { db } from '../db.js';
import { requireUserId } from '../procurement/util.js';
import { monthOf, lastDayOfMonth } from './depositSchedule.js';
import { classifyChannel } from './depositRules.js';


/** Sum ONE month from pre-loaded (already sale-dated) entries. */
function settleMonthFrom(month, entries, channelById) {
  let inHouseCents = 0, deliveryCents = 0;
  for (const e of entries) {
    if (monthOf(e.date) !== month) continue;
    const amt = e.netSalesCents || 0;
    if (classifyChannel(channelById.get(e.channelId)) === 'delivery') deliveryCents += amt;
    else inHouseCents += amt;
  }
  const netSalesCents = inHouseCents + deliveryCents;
  return { month, netSalesCents, inHouseCents, deliveryCents, rawNetSalesCents: netSalesCents };
}

/** Load channels + one entries window covering every requested month's canonical window. */
async function loadSettleContext(months) {
  const userId = await requireUserId();
  const sorted = [...months].sort();
  const from = `${sorted[0]}-01`;
  const to = lastDayOfMonth(sorted[sorted.length - 1]);
  const channels = await db.channels.where('userId').equals(userId).toArray();
  const channelById = new Map(channels.map((c) => [c.channelId, c]));
  const entries = await db.salesEntries
    .where('[userId+date]').between([userId, from], [userId, to], true, true)
    .toArray();
  return { entries, channelById };
}

/**
 * Settled net sales for a calendar month, reconciling deposit-dated payouts back
 * to the month they were earned in by per-channel attribution, WITH an in-house
 * vs delivery split.
 * @param {string} month 'YYYY-MM'
 * @param {{ lagDays?: number }} [opts] lag override for the proration fallback
 *   paths; left unset, each kind uses its own default (see attributeDepositStream)
 * @returns {Promise<{ month:string, netSalesCents:number, inHouseCents:number, deliveryCents:number, rawNetSalesCents:number }>}
 */
export async function settledMonthlyByChannel(month, opts = {}) {
  const { entries, channelById } = await loadSettleContext([month]);
  return settleMonthFrom(month, entries, channelById);
}

/**
 * Settled sales (with the in-house/delivery split and the raw deposit-dated
 * sum) for a SET of months, loading the DB window once. Each month is settled
 * from its own canonical window, so the result for a month is IDENTICAL to
 * settledMonthlyByChannel(month) — screens reading different shapes of this
 * data can never disagree.
 * @param {string[]} months 'YYYY-MM' keys (any order)
 * @returns {Promise<Record<string, { month:string, netSalesCents:number,
 *   inHouseCents:number, deliveryCents:number, rawNetSalesCents:number }>>}
 */
export async function settledByMonthDetailed(months, opts = {}) {
  const out = {};
  if (!months || !months.length) return out;
  const { entries, channelById } = await loadSettleContext(months);
  for (const m of months) out[m] = settleMonthFrom(m, entries, channelById);
  return out;
}

/**
 * Settled net sales for a calendar month (total only). Thin wrapper over
 * settledMonthlyByChannel for callers that don't need the split.
 * @returns {Promise<{ month:string, netSalesCents:number, rawNetSalesCents:number }>}
 */
export async function settledMonthlyNet(month, opts = {}) {
  const r = await settledMonthlyByChannel(month, opts);
  return { month: r.month, netSalesCents: r.netSalesCents, rawNetSalesCents: r.rawNetSalesCents };
}

/**
 * Settled net sales for a SET of months, loading the DB window once. Thin
 * wrapper over settledByMonthDetailed — every month is settled from its own
 * canonical window, so `result[m]` is exactly settledMonthlyNet(m). (A prior
 * version attributed one wide multi-month window in a single pass; the model
 * selection inside attributeDepositStream then depended on the window, and the
 * Sales report's month figure could disagree with the Today tile's. Per-month
 * rounding may drift a cross-month SUM by a cent — cross-screen consistency
 * wins over that cent.)
 * @param {string[]} months  'YYYY-MM' keys to report (any order)
 * @param {{ lagDays?: number }} [opts]  lag override for the proration fallback
 *   paths; left unset, each kind uses its own default (see attributeDepositStream)
 * @returns {Promise<Record<string, number>>} month → settled net cents (rounded)
 */
export async function settledNetByMonth(months, opts = {}) {
  const detailed = await settledByMonthDetailed(months, opts);
  const out = {};
  for (const m of Object.keys(detailed)) out[m] = detailed[m].netSalesCents;
  return out;
}
